/* eslint-disable @typescript-eslint/ban-ts-comment */
import { BrowserTracing } from '@sentry/browser'
import { HttpClient, RewriteFrames } from '@sentry/integrations'
import * as Sentry from '@sentry/react'
import * as buffer from 'buffer'
import { createRoot } from 'react-dom/client'
import {
    BrowserRouter,
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType,
} from 'react-router-dom'
import { useEffect } from 'react'
import App from './App'
import reportWebVitals from './reportWebVitals'
import * as serviceWorkerRegistration from './serviceWorkerRegistration'

declare const VERSION: string

window.Buffer = buffer.Buffer

// Setup sentry
if (process.env.NODE_ENV === 'production') {
    Sentry.init({
        dsn: 'https://ea4907ae76cf6eff43698a8ef63ae5dd@sentry-1.kuknos.ir/3',
        integrations: [
            new BrowserTracing({
                routingInstrumentation: Sentry.reactRouterV6Instrumentation(
                    useEffect,
                    useLocation,
                    useNavigationType,
                    createRoutesFromChildren,
                    matchRoutes
                ),
            }),
            new HttpClient({
                failedRequestStatusCodes: [[500, 599]],
            }),
            new RewriteFrames(),
            new Sentry.Replay(),
        ],

        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampleRate: 1.0, // Capture 100% of the transactions
        // Session Replay
        replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
        environment: process.env.NODE_ENV,
        sendDefaultPii: true,
        release: VERSION,
        normalizeMaxBreadth: 400,
        normalizeDepth: 1000,
    })
}

const rootElement = document.getElementById('root')
const root = createRoot(rootElement!)

root.render(
    <BrowserRouter>
        <App />
    </BrowserRouter>
)

const registerPwaOpeningHandler = (reg: { update: () => void }) => {
    let hidden: string
    let visibilityChange
    if (typeof document.hidden !== 'undefined') {
        // Opera 12.10 and Firefox 18 and later support
        hidden = 'hidden'
        visibilityChange = 'visibilitychange'
        // @ts-ignore
    } else if (typeof document.msHidden !== 'undefined') {
        hidden = 'msHidden'
        visibilityChange = 'msvisibilitychange'
        // @ts-ignore
    } else if (typeof document.webkitHidden !== 'undefined') {
        hidden = 'webkitHidden'
        visibilityChange = 'webkitvisibilitychange'
    }

    // @ts-ignore
    window.document.addEventListener(visibilityChange, () => {
        // @ts-ignore
        if (!document[hidden]) {
            // manually force detection of a potential update when the pwa is opened
            reg.update()
        }
    })

    return reg
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.register({
    onSuccess: (registration) => {
        console.log('on success registration')
        Sentry.captureMessage('Success Update SW')

        registerPwaOpeningHandler(registration)
    },
    onUpdate: (registration) => {
        console.log('on update registration')
        Sentry.captureMessage('Success Receive SW')
        if (registration.waiting) {
            const reg = JSON.parse(JSON.stringify(registration.waiting))
            window.postMessage({ type: 'update_available', reg }, '*')
        }
    },
})

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
